import { Group, Stack } from '@mantine/core';
import { useState } from 'react';
import type { AdherenceLog } from '../../../../api/events-client/events-client.type';
import i18n from '../../../../base-dictionary';
import { inkLighter, secondaryBase, skyDark } from '../../../../styles/design-tokens';
import { Divider } from '../../../../ui-library/divider/divider';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { formatDatetimeToLocal } from '../../../data-management/components/data-management.helpers';
import { TaskMetadataList } from '../../../work-periods/task-detail/task-metadata-list';
import { formatEventType, formatRawEvent } from './adherence-logs-table.helpers';

type Props = {
  adherenceLog: AdherenceLog | null;
  onClose: () => void;
};

export const AdherenceLogSideSection = ({ adherenceLog, onClose }: Props) => {
  const [isMessageExpanded, setIsMessageExpanded] = useState(true);
  const [isAnalysisExpanded, setIsAnalysisExpanded] = useState(true);

  const toggleMessageExpanded = () => setIsMessageExpanded(!isMessageExpanded);
  const toggleAnalysisExpanded = () => setIsAnalysisExpanded(!isAnalysisExpanded);

  if (!adherenceLog) {
    return null;
  }

  const metaData = [
    { label: 'Event Type', value: formatEventType(adherenceLog?.event_type) },
    { label: 'Date', value: formatDatetimeToLocal(adherenceLog?.changed_at) },
    { label: 'Changed By', value: adherenceLog?.changed_by || '-' },
    {
      label: `${i18n.t('common.work_unit')} Field`,
      value: formatRawEvent(adherenceLog?.raw_event),
    },
    { label: 'From', value: adherenceLog?.from_name },
    { label: 'To', value: adherenceLog?.to_name },
  ];

  return (
    <>
      <Group justify="space-between">
        <Group gap={5} align="center">
          <Text color={secondaryBase} weight="bold" size="medium">
            {adherenceLog.task.title}
          </Text>
          <Icon
            name="open_in_new"
            size={20}
            color={secondaryBase}
            onClick={() => window.open(adherenceLog.task.url, '_blank', 'noopener, noreferrer')}
          />
        </Group>
        <Icon name="close" onClick={onClose} color={skyDark} />
      </Group>
      <Divider style={{ margin: '16px 0' }} />
      <Text weight="bold">{adherenceLog.task.name}</Text>
      <Divider style={{ margin: '16px 0' }} />
      <TaskMetadataList metadata={metaData} textColor={inkLighter} />
      <Divider style={{ margin: '16px 0' }} />
      <Stack>
        <Group justify="space-between" style={{ cursor: 'pointer' }} onClick={toggleMessageExpanded}>
          <Text weight="medium" size="medium">
            Message
          </Text>
          <Icon name={isMessageExpanded ? 'expand_less' : 'expand_more'} color={skyDark} />
        </Group>
        {isMessageExpanded && <Text size="small">{adherenceLog.message}</Text>}
        <Divider />
        <Group justify="space-between" style={{ cursor: 'pointer' }} onClick={toggleAnalysisExpanded}>
          <Text weight="medium" size="medium">
            Analysis
          </Text>
          <Icon name={isAnalysisExpanded ? 'expand_less' : 'expand_more'} color={skyDark} />
        </Group>
        {isAnalysisExpanded && <Text size="small">{adherenceLog.analysis}</Text>}
      </Stack>
    </>
  );
};
